/**
 * Stamps cart attributes before checkout so the subscription-shipping function
 * can be matched to a specific test in Partner Dashboard logs.
 */
(function () {
  const SYNC_FLAG = 'subscriptionCheckoutSynced';
  const TRACE_STORAGE_KEY = '47skin-shipping-debug-trace';
  let syncInFlight = null;
  let debounceTimer = null;

  /** Returns true when any cart line carries subscription signals. */
  function hasSubscriptionInCart(cartItems) {
    for (const item of cartItems || []) {
      if (item?.selling_plan_allocation?.selling_plan) {
        return true;
      }

      const properties = item?.properties || {};
      if (
        properties.subscription === 'true' ||
        properties.subscription_id ||
        properties.frequency_num ||
        properties.frequency_type ||
        properties.frequency_type_text
      ) {
        return true;
      }
    }

    return false;
  }

  /** Builds a storefront cart fingerprint for log matching. */
  function buildCartFingerprint(cart) {
    const variantIds = (cart?.items || []).map((item) => item.variant_id).join(',');
    const giftCount = (cart?.items || []).filter((item) => item?.properties?._gift === 'true').length;
    const subscriptionCount = (cart?.items || []).filter(
      (item) => item?.selling_plan_allocation?.selling_plan
    ).length;

    return [
      `lines:${cart?.items?.length ?? 0}`,
      `items:${cart?.item_count ?? 0}`,
      `subtotal:${cart?.items_subtotal_price ?? cart?.total_price ?? 0}`,
      `subs:${subscriptionCount}`,
      `gifts:${giftCount}`,
      `variants:${variantIds || 'none'}`,
    ].join(';');
  }

  /** Creates a unique trace ID for one checkout attempt. */
  function createTraceId() {
    const now = new Date();
    const timestamp = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
      String(now.getHours()).padStart(2, '0'),
      String(now.getMinutes()).padStart(2, '0'),
      String(now.getSeconds()).padStart(2, '0'),
    ].join('');

    const randomPart = Math.random().toString(36).slice(2, 6);
    return `47skin-${timestamp}-${randomPart}`;
  }

  /** Stores and prints the trace ID so you can match Partner Dashboard STDERR. */
  function publishTraceId(traceId) {
    try {
      sessionStorage.setItem(TRACE_STORAGE_KEY, traceId);
    } catch (error) {
      /* ignore */
    }

    console.log(
      `[47skin-shipping] Trace ID: ${traceId} — search Partner Dashboard STDERR for MATCH_THIS_TRACE_ID`
    );
  }

  /** Loads the current cart from the storefront Cart API. */
  async function loadCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`cart.js ${response.status}`);
    }

    return response.json();
  }

  /** Persists checkout debug attributes on the cart. */
  async function updateCheckoutAttributes(cart, options = {}) {
    const shouldHaveSubscription = hasSubscriptionInCart(cart?.items);
    const attributes = {
      subscription_checkout: shouldHaveSubscription ? 'true' : '',
      shipping_debug_cart: buildCartFingerprint(cart),
    };

    if (options.traceId) {
      attributes.shipping_debug_trace = options.traceId;
    }

    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) {
      throw new Error(`cart/update.js ${response.status}`);
    }

    return response.json();
  }

  /** Ensures checkout debug attributes are current, coalescing concurrent calls. */
  async function ensureCheckoutAttributes(cart, options = {}) {
    if (syncInFlight) {
      return syncInFlight;
    }

    syncInFlight = (async () => {
      const snapshot = cart || (await loadCart());
      return updateCheckoutAttributes(snapshot, options);
    })();

    try {
      return await syncInFlight;
    } finally {
      syncInFlight = null;
    }
  }

  /** Returns the checkout button from a click path, including shadow DOM. */
  function getCheckoutButtonFromEvent(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

    for (const node of path) {
      if (!(node instanceof HTMLButtonElement)) {
        continue;
      }

      if (node.name === 'checkout' || node.getAttribute('name') === 'checkout') {
        return node;
      }
    }

    return null;
  }

  /** Returns true when the submit event is initiating Shopify checkout. */
  function isCheckoutSubmit(event) {
    const submitter = event.submitter;
    return (
      submitter?.name === 'checkout' ||
      (submitter instanceof HTMLButtonElement && submitter.getAttribute('name') === 'checkout')
    );
  }

  /** Debounces cart-driven attribute syncs. */
  function scheduleCartSync(cart) {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void ensureCheckoutAttributes(cart);
    }, 150);
  }

  /** Syncs attributes and continues to checkout. */
  async function handleCheckoutStart(form, submitter) {
    const traceId = createTraceId();
    publishTraceId(traceId);

    try {
      await ensureCheckoutAttributes(null, { traceId });
      form.dataset[SYNC_FLAG] = 'true';
      form.requestSubmit(submitter || undefined);
    } catch (error) {
      form.dataset[SYNC_FLAG] = '';
      console.warn('[47skin-shipping]', error);
      form.requestSubmit(submitter || undefined);
    }
  }

  /** Binds storefront listeners that keep checkout debug attributes current. */
  function bindCheckoutDebugSync() {
    if (window.__subscriptionCheckoutSyncBound) {
      return;
    }

    window.__subscriptionCheckoutSyncBound = true;

    document.addEventListener(
      'cart:change',
      (event) => {
        if (event.detail?.cart) {
          scheduleCartSync(event.detail.cart);
        }
      },
      true
    );

    document.addEventListener(
      'variant:add',
      (event) => {
        if (event.detail?.cart) {
          scheduleCartSync(event.detail.cart);
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const checkoutButton = getCheckoutButtonFromEvent(event);
        if (!checkoutButton) {
          return;
        }

        const form = checkoutButton.form;
        if (!(form instanceof HTMLFormElement)) {
          return;
        }

        if (form.dataset[SYNC_FLAG] === 'true') {
          form.dataset[SYNC_FLAG] = '';
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        void handleCheckoutStart(form, checkoutButton);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !isCheckoutSubmit(event)) {
          return;
        }

        if (form.dataset[SYNC_FLAG] === 'true') {
          form.dataset[SYNC_FLAG] = '';
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        void handleCheckoutStart(form, event.submitter || undefined);
      },
      true
    );
  }

  bindCheckoutDebugSync();
})();
